import { DateTime } from '@ksyos/date-time';
import { isDate, isNil } from 'lodash';
import { Timezone } from '@common/enums/timezone';

type Dates = (string | undefined | null | Date)[];

type IsWeekendOptions = {
    timezone?: string;
};

export type DateLike = DateTime | Date | string;

export function findMinDate(values: Dates) {
    const dates = mapValuesToDateTimes(values);
    const datesExcludingNull = dates.filter((date) => date !== null) as DateTime[];
    const minDate = DateTime.min(datesExcludingNull);

    return minDate ? minDate.toDate() : undefined;
}

export function findMaxDate(values: Dates, isIncludingNull?: boolean) {
    const dates = mapValuesToDateTimes(values, isIncludingNull);

    if (dates.some((date) => date === null)) {
        return null;
    }

    const datesExcludingNull = dates.filter((date) => date !== null) as DateTime[];
    const maxDate = DateTime.max(datesExcludingNull);

    return maxDate ? maxDate.toDate() : undefined;
}

function mapValuesToDateTimes(dates: Dates, isIncludingNull?: boolean) {
    const result: (DateTime | null)[] = [];

    for (const date of dates) {
        if (isIncludingNull && date === null) {
            result.push(date);
        } else if (date) {
            const dateTime = DateTime.from(date);
            if (dateTime.isValid()) {
                result.push(dateTime);
            }
        }
    }

    return result;
}

export function isDateInBetweenTwoDates(
    date?: string | Date | null,
    startInterval?: string | Date | null,
    endInterval?: string | Date | null
) {
    if (!startInterval) {
        return false;
    }
    if (!date && !endInterval) {
        return true;
    } else if (!endInterval) {
        return DateTime.from(date!).isSameOrAfter(startInterval);
    } else if (!date) {
        return DateTime.now().isSameOrBefore(endInterval);
    } else {
        return DateTime.from(date).isSameOrAfter(startInterval) && DateTime.from(date).isSameOrBefore(endInterval);
    }
}

export function sortArrayOnStartDate<T extends { startDate: Date | string }>(
    array: T[],
    sortDirection: 'asc' | 'desc'
) {
    const direction = sortDirection === 'asc' ? 1 : -1;

    return array.sort((a, b) => DateTime.from(a.startDate).diff(b.startDate) * direction);
}

export function dateIsBeforeMinDate(date: string | null, minDate?: string | Date | null) {
    return !!(date && minDate && DateTime.from(date).isBefore(DateTime.from(minDate)));
}

export function dateIsAfterMaxDate(date: string | null, maxDate?: string | Date | null) {
    if (!date && maxDate) {
        return true;
    }

    return !!(date && maxDate && DateTime.from(date).isAfter(DateTime.from(maxDate)));
}

export function isWeekend(date: DateTime, options: IsWeekendOptions = {}) {
    const timezone = options.timezone ?? 'Europe/Amsterdam';

    return date.tz(timezone, true).isWeekend();
}

export function isSameDate(date: Date | string | null | undefined, otherDate: Date | string | null | undefined) {
    if (isNil(date)) {
        return isNil(otherDate);
    }

    if (isNil(otherDate)) {
        return isNil(date);
    }

    return DateTime.from(date).isSame(otherDate);
}

export function isDateOrString(value: unknown): value is string | Date {
    return typeof value === 'string' || isDate(value);
}

export function sortByPerformedDatetime<T extends { performedDatetime: string | Date | null }>(
    sortDirection: 'asc' | 'desc' = 'asc'
) {
    return function (a: T, b: T) {
        if (a.performedDatetime === null || b.performedDatetime === null) {
            throw new Error('Expected performedDatetime to be defined');
        }

        if (sortDirection === 'desc') {
            return DateTime.from(b.performedDatetime).unix() - DateTime.from(a.performedDatetime).unix();
        }

        return DateTime.from(a.performedDatetime).unix() - DateTime.from(b.performedDatetime).unix();
    };
}

export function getWorkingDaysBetweenDates(start: DateLike, end: DateLike): number {
    let count = 0;

    start = DateTime.from(start);
    end = DateTime.from(end);

    if (!start.isValid() || !end.isValid()) {
        return count;
    }

    const startDate = start.startOf('day');
    const endDate = end.startOf('day');

    const diffDays = endDate.diff(startDate, 'days');

    const restDays = diffDays % 7;
    const weeksDays = ((diffDays - restDays) / 7) * 5;

    count += weeksDays;

    for (let i = 0; i < restDays; i++) {
        const day = startDate.day(startDate.day() + i).day();
        // 0 = Sunday, 6 = Saturday
        if (day !== 0 && day !== 6) {
            count++;
        }
    }

    return Math.max(count, 0);
}

export function formatDateAsYYYYMMDD(date: Date | string, separator: '-' | '' = '-') {
    return DateTime.from(date).format(`YYYY${separator}MM${separator}DD`);
}

export function getLocalDateTime(date: DateLike) {
    assertDateLike(date);

    // Convert string to local timezone if it is in the format 'YYYY-MM-DD' or 'YYYY-MM-DD HH:mm:ss'
    const keepCurrentTimeWhenConvertingTimezone = Boolean(
        typeof date === 'string' && date.match(/^\d{4}-\d{2}-\d{2}( \d{2}:\d{2}:\d{2})?$/)
    );

    return DateTime.from(date).tz(Timezone.Amsterdam, keepCurrentTimeWhenConvertingTimezone);
}

export function parseLocalDateTime(datetime: DateLike) {
    const localDateTime = getLocalDateTime(datetime);

    if (!localDateTime.isValid()) {
        throw new Error('Invalid datetime');
    }

    return localDateTime;
}

export function parseWorkingDaysBetweenDates(start: DateLike, end: DateLike) {
    const parsedStart = parseLocalDateTime(start);
    const parsedEnd = parseLocalDateTime(end);

    return getWorkingDaysBetweenDates(parsedStart, parsedEnd);
}

export function isDateLike(value: unknown): value is DateLike {
    return value instanceof DateTime || value instanceof Date || typeof value === 'string';
}

export function assertDateLike(value: unknown): asserts value is DateLike {
    if (!isDateLike(value)) {
        throw new Error('Expected value to be a DateTime, Date or string');
    }
}

export function hasActiveItem(items: { endDate: Date | string | null; startDate: Date | string }[], date = new Date()) {
    return items.some(
        ({ endDate, startDate }) =>
            DateTime.from(startDate).isSameOrBefore(date) && (!endDate || DateTime.from(endDate).isSameOrAfter(date))
    );
}

export function findActiveItem<T extends { endDate: Date | string | null; startDate: Date | string }>(
    items: T[],
    date: string | Date = new Date()
): T | undefined {
    return items.find(
        ({ endDate, startDate }) =>
            DateTime.from(startDate).isSameOrBefore(date) && (!endDate || DateTime.from(endDate).isSameOrAfter(date))
    );
}
